// FullscreenSection — give a content block a "maximize" affordance so an
// operator can take it over the whole viewport without losing component state.
//
// Why a portal: rendering into document.body lets the overlay sit above the
// Shell (sidebar, page-head, tabs) without any z-index gymnastics or layout
// reflows. The React subtree is the SAME component instance in both states —
// only its DOM position moves — so React Query caches, scroll positions, and
// in-flight mutations all survive the toggle.
//
// Used by: chit-detail.tsx (ledger card + payout-schedule card).

import { useEffect, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.js';

interface WrapProps {
  is_fullscreen: boolean;
  on_exit: () => void;
  children: ReactNode;
  /** Optional aria label for the dialog when active. */
  label?: string;
}

/**
 * When `is_fullscreen` is false this is a transparent pass-through. When true
 * it portals `children` into a fixed full-viewport overlay, locks body scroll,
 * and wires Escape to `on_exit`. The caller renders its own header (with a
 * <FullscreenToggle/>) inside `children` so the same UI shows in both modes.
 */
export function FullscreenWrap({ is_fullscreen, on_exit, children, label }: WrapProps): ReactElement {
  useEffect(() => {
    if (!is_fullscreen) return;
    const on_key = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') on_exit();
    };
    document.addEventListener('keydown', on_key);
    // Background scroll-lock — without this the page behind the overlay can
    // scroll on touch devices and the overlay's own scroll area gets confused.
    const prev_overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', on_key);
      document.body.style.overflow = prev_overflow;
    };
  }, [is_fullscreen, on_exit]);

  if (!is_fullscreen) return <>{children}</>;

  return createPortal(
    <div className="fs-overlay" role="dialog" aria-modal="true" aria-label={label}>
      {children}
    </div>,
    document.body,
  );
}

interface ToggleProps {
  is_fullscreen: boolean;
  on_toggle: () => void;
  /** Pass-through for inline placement next to a card title. */
  className?: string;
}

/**
 * The visible icon button. Placed inside the section's own header so it
 * appears in both inline and fullscreen modes — clicking it flips back.
 */
export function FullscreenToggle({ is_fullscreen, on_toggle, className }: ToggleProps): ReactElement {
  return (
    <button
      type="button"
      className={'btn ghost sm fs-toggle' + (className ? ' ' + className : '')}
      onClick={on_toggle}
      title={is_fullscreen ? 'Exit fullscreen (Esc)' : 'Open fullscreen'}
      aria-label={is_fullscreen ? 'Exit fullscreen' : 'Open fullscreen'}
    >
      {is_fullscreen ? <Icon.minimize /> : <Icon.maximize />}
    </button>
  );
}
